/**
 * Farmer search on the village roster (F4.2, R25): prefix match on name tokens, in Hindi or English
 * (Devanagari is romanised and both sides are compared on a consonant skeleton, so 'कालू' finds
 * 'Kalu Ram'), or an exact roster number; the phone's last 4 digits narrow the list.
 */
const VOWELS: Record<string, string> = {
  अ: 'a',
  आ: 'aa',
  इ: 'i',
  ई: 'ee',
  उ: 'u',
  ऊ: 'oo',
  ऋ: 'ri',
  ए: 'e',
  ऐ: 'ai',
  ओ: 'o',
  औ: 'au',
}
const SIGNS: Record<string, string> = {
  'ा': 'aa',
  'ि': 'i',
  'ी': 'ee',
  'ु': 'u',
  'ू': 'oo',
  'ृ': 'ri',
  'े': 'e',
  'ै': 'ai',
  'ो': 'o',
  'ौ': 'au',
  'ं': 'n',
  'ँ': 'n',
  'ः': 'h',
  '्': '',
  '़': '',
}
const CONS: Record<string, string> = {
  क: 'k',
  ख: 'kh',
  ग: 'g',
  घ: 'gh',
  ङ: 'n',
  च: 'ch',
  छ: 'chh',
  ज: 'j',
  झ: 'jh',
  ञ: 'n',
  ट: 't',
  ठ: 'th',
  ड: 'd',
  ढ: 'dh',
  ण: 'n',
  त: 't',
  थ: 'th',
  द: 'd',
  ध: 'dh',
  न: 'n',
  प: 'p',
  फ: 'ph',
  ब: 'b',
  भ: 'bh',
  म: 'm',
  य: 'y',
  र: 'r',
  ल: 'l',
  व: 'v',
  श: 'sh',
  ष: 'sh',
  स: 's',
  ह: 'h',
  क़: 'q',
  ख़: 'kh',
  ग़: 'g',
  ज़: 'z',
  ड़: 'r',
  ढ़: 'rh',
  फ़: 'f',
}

/** Rough Devanagari → Latin; Latin passes through lower-cased. */
export function romanise(s: string): string {
  let out = ''
  const chars = [...s.normalize('NFC')]
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i]!
    if (CONS[c] !== undefined) {
      const next = chars[i + 1]
      out += CONS[c]
      // inherent 'a' unless a vowel sign or virama follows
      if (next === undefined || (SIGNS[next] === undefined && next !== ' '))
        out += next === undefined ? '' : 'a'
    } else if (VOWELS[c] !== undefined) out += VOWELS[c]
    else if (SIGNS[c] !== undefined) out += SIGNS[c]
    else out += c.toLowerCase()
  }
  return out
}

/** Consonant skeleton used to compare spellings: 'Kaalu' / 'Kalu' / 'कालू' → 'kl'. */
export function skeleton(s: string): string {
  return romanise(s)
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/(sh|ch|th|dh|bh|kh|gh|ph|jh)/g, (m) => m[0]!)
    .replace(/[aeiouyw]/g, '')
    .replace(/v/g, 'b')
    .replace(/(.)\1+/g, '$1')
}

function tokens(s: string): string[] {
  return s.split(/\s+/).filter(Boolean)
}

export interface SearchableFarmer {
  name: string
  father_name?: string | null
  roster_no: string
  phone_last4?: string | null
}

/** Does `query` (one or more words) prefix-match the person's name tokens? */
export function nameMatches(name: string, query: string): boolean {
  const q = tokens(query)
  if (q.length === 0) return true
  const nameToks = tokens(name)
  const latin = nameToks.map((t) => romanise(t).toLowerCase())
  const skel = nameToks.map(skeleton)
  return q.every((qt) => {
    const ql = romanise(qt).toLowerCase()
    const qs = skeleton(qt)
    return (
      latin.some((t) => t.startsWith(ql)) || (qs.length > 0 && skel.some((t) => t.startsWith(qs)))
    )
  })
}

/** Roster numbers compare without leading zeros or a village prefix: '17' finds 'BELU-017'. */
/**
 * His number as typed on the keypad: the digits of the last part of his farmer ID, without leading
 * zeros (RJ-AMOD-U001 → 1, VELU-017 → 17). The keypad has digits only.
 */
export function rosterDigits(rosterNo: string): string {
  return rosterNo.replace(/^.*-/, '').replace(/\D/g, '').replace(/^0+/, '')
}

export function rosterNoMatches(rosterNo: string, typed: string): boolean {
  const b = rosterDigits(typed.trim())
  return b !== '' && rosterDigits(rosterNo) === b
}

export function findFarmers<F extends SearchableFarmer>(
  list: readonly F[],
  q: { name?: string; rosterNo?: string; last4?: string },
): { results: F[]; method: 'SEARCH' | 'ROSTER_NO' } {
  if (q.rosterNo?.trim()) {
    return {
      results: list.filter((f) => rosterNoMatches(f.roster_no, q.rosterNo!)),
      method: 'ROSTER_NO',
    }
  }
  const name = q.name?.trim() ?? ''
  if (!name) return { results: [], method: 'SEARCH' }
  let results = list.filter((f) => nameMatches(f.name, name))
  const last4 = q.last4?.trim() ?? ''
  if (/^\d{4}$/.test(last4)) {
    const narrowed = results.filter((f) => f.phone_last4 === last4)
    // A changed phone must not hide him: keep the name matches when nobody has those digits.
    if (narrowed.length > 0) results = narrowed
  }
  return { results, method: 'SEARCH' }
}

/**
 * The V1 find list: a farmer matches by name, father's name or farmer ID (or its digits, U001 → 1).
 * An empty query matches everyone.
 */
export function farmerMatches(
  f: { name: string; father_name?: string | null; roster_no: string },
  q: string,
): boolean {
  const t = q.trim()
  if (!t) return true
  if (nameMatches(f.name, t)) return true
  if (f.father_name && nameMatches(f.father_name, t)) return true
  if (f.roster_no.toLowerCase().includes(t.toLowerCase())) return true
  return /^\d+$/.test(t) && rosterNoMatches(f.roster_no, t)
}
